import { DOCUMENTS_HUB_ENABLED, HR_HUB_ENABLED } from './featureFlags';

/**
 * Configurazione Centralizzata Endpoints
 *
 * Gli URL dei backend sono risolti in quest'ordine:
 *   1. variabili d'ambiente Vite (VITE_*) - iniettate da .env.{development,production}
 *   2. fallback hardcoded di produzione (safety net)
 *
 * Ambienti supportati:
 *   - development / staging  -> .env.development  -> backend su nexus-dev-4e653
 *   - production             -> .env.production   -> backend su cdg-serverless
 *   - locale override        -> .env.local        -> localhost:* per dev offline
 */

const env = typeof import.meta !== 'undefined' && typeof import.meta.env !== 'undefined'
  ? import.meta.env
  : ({} as ImportMetaEnv);

const isProd = env.PROD === true;
const DEBUG = !isProd && env.VITE_API_DEBUG === 'true';

function assertEnv(key: string): string {
  const value = env[key];
  if (!value) {
    const msg = isProd
      ? `[ENV] ${key} mancante in produzione. Verifica il CI/CD secrets.`
      : `[ENV] ${key} mancante in development. Copia frontend/.env.local.example in frontend/.env.local e riempi i valori.`;
    throw new Error(msg);
  }
  return value;
}

// Environment Guard: fail fast in development if required vars are missing
if (!isProd) {
  const required = [
    'VITE_FIREBASE_PROJECT_ID',
    'VITE_FIREBASE_AUTH_DOMAIN',
    'VITE_ADMIN_SERVICE_URL',
    'VITE_PYTHON_BACKEND_URL',
    'VITE_PYTHON_BACKEND_DIPENDENZE_URL'
  ];
  if (DOCUMENTS_HUB_ENABLED) required.push('VITE_DOCS_API_URL');
  if (HR_HUB_ENABLED) required.push('VITE_HR_API_URL');
  const missing = required.filter(k => !env[k]);
  if (missing.length > 0) {
    throw new Error(
      `[ENV] Variabili d'ambiente mancanti in development: ${missing.join(', ')}. ` +
      `Copia frontend/.env.local.example in frontend/.env.local e riempi i valori.`
    );
  }
}

/**
 * Admin Service URL (Cloud Run europe-west1)
 */
export const ADMIN_SERVICE_URL = assertEnv('VITE_ADMIN_SERVICE_URL');

/**
 * Python Backend principale (Cloud Run europe-west8)
 * Include servizio Vertex AI con Gemini
 */
export const PYTHON_BACKEND_URL = assertEnv('VITE_PYTHON_BACKEND_URL');

/**
 * Alias storico — in passato python-backend era su porta 8081 separato.
 * Ora e' unificato con PYTHON_BACKEND_URL.
 */
export const PYTHON_BACKEND_MAIN_URL = PYTHON_BACKEND_URL;

/**
 * Python Backend Dipendenze (Cloud Run europe-west8)
 */
export const PYTHON_BACKEND_DIPENDENZE_URL = assertEnv('VITE_PYTHON_BACKEND_DIPENDENZE_URL');

/**
 * Document Platform API. Required only when the Documents Hub is enabled.
 */
export const DOCS_API_URL = DOCUMENTS_HUB_ENABLED ? assertEnv('VITE_DOCS_API_URL') : (env.VITE_DOCS_API_URL || '');
export const HR_API_URL = HR_HUB_ENABLED ? assertEnv('VITE_HR_API_URL') : (env.VITE_HR_API_URL || '');

const hrApiBase = `${HR_API_URL.replace(/\/+$/, '')}/v1`;
export const HR_API_ENDPOINTS = {
  slips: `${hrApiBase}/cedolini`,
  slip: (id: string) => `${hrApiBase}/cedolini/${encodeURIComponent(id)}`,
  importBatches: `${hrApiBase}/import-batches`,
  importBatch: (id: string) => `${hrApiBase}/import-batches/${encodeURIComponent(id)}`,
  completeImportBatch: (id: string) => `${hrApiBase}/import-batches/${encodeURIComponent(id)}/complete`,
  importBatchErrors: (id: string) => `${hrApiBase}/import-batches/${encodeURIComponent(id)}/errors`,
  importScopeOptions: `${hrApiBase}/access-scope-options`,
  inailPat: `${hrApiBase}/inail-pat`,
  inailPatEntry: (id: string) => `${hrApiBase}/inail-pat/${encodeURIComponent(id)}`,
  companyParameters: `${hrApiBase}/company-parameters`,
  companyParameterTargets: `${hrApiBase}/company-parameters/targets`,
  companyParameter: (id: string) => `${hrApiBase}/company-parameters/${encodeURIComponent(id)}`,
  turniScopeOptions: `${hrApiBase}/turni/scope-options`,
  turniStrutture: `${hrApiBase}/turni/strutture`,
  turniStruttura: (id: string) => `${hrApiBase}/turni/strutture/${encodeURIComponent(id)}`,
  turniStrutturaConfig: (id: string) => `${hrApiBase}/turni/strutture/${encodeURIComponent(id)}/config`,
  turniOperatori: (id: string) => `${hrApiBase}/turni/strutture/${encodeURIComponent(id)}/operatori`,
  turniOperatore: (id: string, opId: string) => `${hrApiBase}/turni/strutture/${encodeURIComponent(id)}/operatori/${encodeURIComponent(opId)}`,
  turniOperatoriSync: (id: string) => `${hrApiBase}/turni/strutture/${encodeURIComponent(id)}/operatori/sync-equipe`,
  turniOperatoriDefaults: (id: string) => `${hrApiBase}/turni/strutture/${encodeURIComponent(id)}/operatori/defaults`,
  turniAssenze: (id: string, anno: number, mese: number) => `${hrApiBase}/turni/strutture/${encodeURIComponent(id)}/assenze?anno=${anno}&mese=${mese}`,
  turniRateiProiezione: (id: string, anno: number) => `${hrApiBase}/turni/strutture/${encodeURIComponent(id)}/ratei-proiezione?anno=${anno}`,
  turniMese: (id: string, anno: number, mese: number) => `${hrApiBase}/turni/strutture/${encodeURIComponent(id)}/mese/${anno}/${mese}`,
  turniGenera: (id: string) => `${hrApiBase}/turni/strutture/${encodeURIComponent(id)}/genera`,
  turniVerifica: (id: string) => `${hrApiBase}/turni/strutture/${encodeURIComponent(id)}/verifica-fattibilita`,
  turniPiano: (id: string, anno: number, mese: number) => `${hrApiBase}/turni/strutture/${encodeURIComponent(id)}/piani/${anno}/${mese}`,
  turniPianoUpdate: (planId: string) => `${hrApiBase}/turni/piani/${encodeURIComponent(planId)}`,
  turniSostituzioni: (planId: string) => `${hrApiBase}/turni/piani/${encodeURIComponent(planId)}/sostituzioni`,
} as const;

const docsApiBase = `${DOCS_API_URL.replace(/\/+$/, '')}/v1`;
export const DOCS_API_ENDPOINTS = {
  catalog: `${docsApiBase}/catalog`,
    documents: `${docsApiBase}/documents`,
    consoleDocuments: `${docsApiBase}/console/documents`,
    consoleDocument: (id: string) =>
      `${docsApiBase}/console/documents/${encodeURIComponent(id)}`,
    documentCode: (id: string) =>
      `${docsApiBase}/console/documents/${encodeURIComponent(id)}/code`,
    documentCodePreview: (id: string) =>
      `${docsApiBase}/console/documents/${encodeURIComponent(id)}/code-preview`,
    documentArchive: (id: string) =>
      `${docsApiBase}/console/documents/${encodeURIComponent(id)}/archive`,
    documentRestore: (id: string) =>
      `${docsApiBase}/console/documents/${encodeURIComponent(id)}/restore`,
    documentDelete: (id: string) =>
      `${docsApiBase}/console/documents/${encodeURIComponent(id)}/delete`,
    documentDeletionRequest: (id: string) =>
      `${docsApiBase}/console/documents/${encodeURIComponent(id)}/deletion`,
    documentDeletionRequests: `${docsApiBase}/console/deletion-requests`,
    retryDocumentDeletion: (requestId: string) =>
      `${docsApiBase}/console/deletion-requests/${encodeURIComponent(requestId)}/retry`,
  createIntake: `${docsApiBase}/documents/intake`,
  intakes: `${docsApiBase}/console/intakes`,
  document: (id: string) => `${docsApiBase}/documents/${encodeURIComponent(id)}`,
  documentIntake: (id: string) =>
    `${docsApiBase}/documents/${encodeURIComponent(id)}/intake`,
  intakeCodeAvailability: (id: string) =>
    `${docsApiBase}/documents/${encodeURIComponent(id)}/intake/code-availability`,
  confirmIntake: (id: string) =>
    `${docsApiBase}/documents/${encodeURIComponent(id)}/intake/confirm`,
  intakeDraft: (id: string) =>
    `${docsApiBase}/documents/${encodeURIComponent(id)}/intake/draft`,
  intakePreview: (id: string) =>
    `${docsApiBase}/documents/${encodeURIComponent(id)}/intake/preview`,
  retryIntake: (id: string) =>
    `${docsApiBase}/documents/${encodeURIComponent(id)}/intake/retry`,
  documentLinks: (id: string) =>
    `${docsApiBase}/documents/${encodeURIComponent(id)}/links`,
  consoleDocumentLinks: (id: string) =>
    `${docsApiBase}/console/documents/${encodeURIComponent(id)}/links`,
  documentReferences: (id: string) =>
    `${docsApiBase}/console/documents/${encodeURIComponent(id)}/references`,
  reanalyzeDocumentReferences: (id: string) =>
    `${docsApiBase}/console/documents/${encodeURIComponent(id)}/references/reanalyze`,
  ignorePendingReference: (id: string, referenceId: string) =>
    `${docsApiBase}/console/documents/${encodeURIComponent(id)}/references/${encodeURIComponent(referenceId)}`,
  linkPendingReference: (id: string, referenceId: string) =>
    `${docsApiBase}/console/documents/${encodeURIComponent(id)}/references/${encodeURIComponent(referenceId)}/link`,
  consoleDocumentSearch: `${docsApiBase}/console/document-search`,
  decideDocumentLinks: (id: string) =>
    `${docsApiBase}/documents/${encodeURIComponent(id)}/links/decisions`,
  linksSummary: `${docsApiBase}/console/links/summary`,
  linksMissingCodes: `${docsApiBase}/console/links/missing-codes`,
  linksMissingCodeCitations: `${docsApiBase}/console/links/missing-codes/citations`,
  linksMissingCodeLink: `${docsApiBase}/console/links/missing-codes/link`,
  linksMissingCodeIgnore: `${docsApiBase}/console/links/missing-codes/ignore`,
  linksToValidate: `${docsApiBase}/console/links/to-validate`,
  linksAutoConfirmed: `${docsApiBase}/console/links/auto-confirmed`,
  linksDecisions: `${docsApiBase}/console/links/decisions`,
  linkUndoAutoConfirm: (linkId: string) =>
    `${docsApiBase}/console/links/${encodeURIComponent(linkId)}/undo-auto-confirm`,
  linkAudits: `${docsApiBase}/console/link-audits`,
  linkAudit: (auditId: string) =>
    `${docsApiBase}/console/link-audits/${encodeURIComponent(auditId)}`,
  linkAuditExport: (auditId: string) =>
    `${docsApiBase}/console/link-audits/${encodeURIComponent(auditId)}/export`,
  documentExternalReferences: (id: string) =>
    `${docsApiBase}/console/documents/${encodeURIComponent(id)}/external-references`,
  originalDownload: (id: string) =>
    `${docsApiBase}/console/documents/${encodeURIComponent(id)}/original-download`,
  documentVersions: (id: string) => `${docsApiBase}/documents/${encodeURIComponent(id)}/versions`,
  completeUpload: (id: string, versionId: string) =>
    `${docsApiBase}/documents/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}/complete`,
  download: (id: string) => `${docsApiBase}/documents/${encodeURIComponent(id)}/download`,
  taxonomies: `${docsApiBase}/console/taxonomies`,
  taxonomy: (id: string) => `${docsApiBase}/console/taxonomies/${encodeURIComponent(id)}`,
  taxonomyArchive: (id: string) => `${docsApiBase}/console/taxonomies/${encodeURIComponent(id)}/archive`,
  taxonomyTerms: (id: string) => `${docsApiBase}/console/taxonomies/${encodeURIComponent(id)}/terms`,
  term: (id: string) => `${docsApiBase}/console/terms/${encodeURIComponent(id)}`,
  termAliases: (id: string) => `${docsApiBase}/console/terms/${encodeURIComponent(id)}/aliases`,
  termAlias: (id: string) => `${docsApiBase}/console/term-aliases/${encodeURIComponent(id)}`,
  termArchive: (id: string) => `${docsApiBase}/console/terms/${encodeURIComponent(id)}/archive`,
  documentTypes: `${docsApiBase}/console/document-types`,
  documentType: (id: string) => `${docsApiBase}/console/document-types/${encodeURIComponent(id)}`,
  documentTypeAliases: (id: string) =>
    `${docsApiBase}/console/document-types/${encodeURIComponent(id)}/aliases`,
  documentTypeAlias: (id: string) =>
    `${docsApiBase}/console/document-type-aliases/${encodeURIComponent(id)}`,
  documentTypeArchive: (id: string) =>
    `${docsApiBase}/console/document-types/${encodeURIComponent(id)}/archive`,
} as const;

/**
 * Firebase Functions Region
 * Admin Service + Cloud Functions sono in europe-west1,
 * mentre i backend Python Cloud Run restano in europe-west8.
 */
export const FUNCTIONS_REGION = 'europe-west1';

/**
 * Firebase Project ID — dipende dall'ambiente
 */
export const FIREBASE_PROJECT_ID = assertEnv('VITE_FIREBASE_PROJECT_ID');

/**
 * Helper per costruire URL Cloud Functions
 */
export function getFunctionUrl(functionName: string): string {
  if (!isProd && env.VITE_USE_EMULATORS === 'true') {
    return `http://localhost:5001/${FIREBASE_PROJECT_ID}/${FUNCTIONS_REGION}/${functionName}`;
  }
  return `https://${FUNCTIONS_REGION}-${FIREBASE_PROJECT_ID}.cloudfunctions.net/${functionName}`;
}

/**
 * Endpoint Admin Service
 */
export const ADMIN_ENDPOINTS = {
  // Users
  listUsers: `${ADMIN_SERVICE_URL}/users/list`,
  getUserPermissions: (uid: string) => `${ADMIN_SERVICE_URL}/users/${uid}/permissions`,
  updateUserPermissions: (uid: string) => `${ADMIN_SERVICE_URL}/users/${uid}/permissions`,
  deleteUserPermissions: (uid: string) => `${ADMIN_SERVICE_URL}/users/${uid}/permissions`,
  
  // Notifications
  notifications: `${ADMIN_SERVICE_URL}/notifications`,
  notificationPreferences: `${ADMIN_SERVICE_URL}/notifications/preferences`,
  notificationCount: `${ADMIN_SERVICE_URL}/notifications/count`,
  fcmToken: `${ADMIN_SERVICE_URL}/notifications/fcm-token`,
  securityEvent: `${ADMIN_SERVICE_URL}/notifications/security-event`,
  
  // Audit
  auditLogs: `${ADMIN_SERVICE_URL}/audit/logs`,
  
  // Maintenance
  maintenance: `${ADMIN_SERVICE_URL}/maintenance`,

  // Access Control (read-only per componenti operativi)
  workspaceInfo: `${ADMIN_SERVICE_URL}/access-control/workspace-info`,
  
  // Sync
  syncWorkspaceGroups: `${ADMIN_SERVICE_URL}/syncWorkspaceGroups`,
  syncWorkspaceUsers: `${ADMIN_SERVICE_URL}/syncWorkspaceUsers`,
} as const;

/**
 * Log configurazione in development
 */
if (DEBUG) {
  console.log('🔧 Endpoints Configuration:', {
    adminService: ADMIN_SERVICE_URL,
    pythonBackend: PYTHON_BACKEND_URL,
    dipendenze: PYTHON_BACKEND_DIPENDENZE_URL,
    projectId: FIREBASE_PROJECT_ID,
    functionsRegion: FUNCTIONS_REGION,
    environment: 'development'
  });
}

// Environment isolation: se arriviamo qui in dev, le required env sono presenti.
// Se qualche variabile è undefined in produzione, il build CI deve fallire prima del deploy.
